/** PersonChip — hoverable employee name with role-scoped peek card.
 * Everyone: identity, live free/busy, org info. HR/reporting manager: performance insights.
 * Click → opens the full Employee 360 profile popup. */
import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useEmployeeProfile } from "@/lib/employeeProfile";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Trophy, ListChecks, AlertTriangle, CalendarClock, Sparkles } from "lucide-react";

const STATUS_META = {
  clocked_in: { dot: "bg-emerald-500", label: "Clocked in" },
  done_for_day: { dot: "bg-sky-500", label: "Done for the day" },
  on_leave: { dot: "bg-red-500", label: "On leave" },
  day_off: { dot: "bg-foreground/30", label: "Day off" },
  not_in_yet: { dot: "bg-amber-500", label: "Not clocked in" },
};

const cache = new Map(); // uid → {at, data}
const TTL = 60000;

export function PersonChip({ userId, name, person, size, testid, className = "", children }) {
  // Feb 2026 R113 · Accept a `person` object shorthand so callers can
  // pass `{ id, name, email, avatar_data_url }` — how the points
  // ledger + leaderboard endpoints already shape their responses. Prior
  // signature only read `userId`/`name`, so any `<PersonChip person={..} />`
  // rendered an empty span, which is exactly the "no names on Points
  // History / Leaderboard" bug production reported.
  const effectiveUserId = userId || person?.id || person?.user_id;
  const effectiveName = name || person?.name || person?.email;
  const { activeWorkspaceId: wsId } = useAuth();
  const { open: openProfile } = useEmployeeProfile();
  const [data, setData] = useState(null);

  const fetchPeek = async (open) => {
    if (!open || !effectiveUserId || !wsId) return;
    const hit = cache.get(effectiveUserId);
    if (hit && Date.now() - hit.at < TTL) { setData(hit.data); return; }
    try {
      const { data: d } = await api.get(`/workspaces/${wsId}/members/${effectiveUserId}/peek`);
      cache.set(effectiveUserId, { at: Date.now(), data: d });
      setData(d);
    } catch { setData({ name: effectiveName, error: true }); }
  };

  const label = children || effectiveName || (effectiveUserId ? "Unknown user" : "—");
  if (!effectiveUserId) return <span className={className} data-testid={testid}>{label}</span>;
  return (
    <HoverCard openDelay={250} closeDelay={100} onOpenChange={fetchPeek}>
      <HoverCardTrigger asChild>
        <span
          className={`cursor-pointer underline decoration-dotted decoration-foreground/30 underline-offset-2 hover:decoration-solid ${size === "sm" ? "text-xs" : ""} ${className}`}
          data-testid={testid || `person-chip-${effectiveUserId}`}
          onClick={(e) => { e.stopPropagation(); openProfile(effectiveUserId); }}
        >
          {label}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-80 rounded-none p-0" data-testid={`person-peek-${effectiveUserId}`}>
        {!data ? <div className="p-4 text-xs text-muted-foreground">Loading…</div> : <PeekBody d={data} onOpenProfile={() => openProfile(effectiveUserId)} />}
      </HoverCardContent>
    </HoverCard>
  );
}

function PeekBody({ d, onOpenProfile }) {
  if (d.error) return <div className="p-4 text-xs text-muted-foreground">Couldn't load profile.</div>;
  const st = STATUS_META[d.status] || STATUS_META.not_in_yet;
  return (
    <div>
      <div className="p-3 flex items-start gap-3 border-b">
        <Avatar className="w-10 h-10 rounded-none">
          {d.avatar_data_url && <AvatarImage src={d.avatar_data_url} />}
          <AvatarFallback className="rounded-none">{(d.name || "?").slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold truncate">{d.name}</p>
          <p className="text-xs text-muted-foreground truncate">
            {[d.designation, d.department].filter(Boolean).join(" · ") || d.email}
          </p>
          <div className="flex items-center gap-1.5 mt-1">
            <span className={`w-2 h-2 rounded-full ${st.dot}`} />
            <span className="text-xs">{st.label}{d.wfh_today ? " · WFH" : ""}</span>
          </div>
        </div>
      </div>
      <div className="px-3 py-2 text-xs text-muted-foreground border-b flex justify-between items-center gap-2">
        <span className="truncate">{d.manager_name ? `Reports to ${d.manager_name}` : "—"}</span>
        {/* Oct 2026 · Shift timing — now shows name + window so a
            viewer can distinguish General from Early / Late / Night
            shifts at a glance. Reads the shift resolved by
            `_user_shift` on the backend (employee.shift_id →
            workspace default). */}
        {d.shift?.start && (
          <span className="shrink-0 inline-flex items-center gap-1" data-testid="peek-shift">
            <CalendarClock className="w-3 h-3 text-muted-foreground" />
            <span className="font-mono text-foreground/80">{d.shift.start}–{d.shift.end}</span>
            {d.shift.name && d.shift.name !== "General" && (
              <span className="text-[10px] text-muted-foreground">· {d.shift.name}</span>
            )}
          </span>
        )}
      </div>
      {d.rich && (
        <div className="p-3 space-y-2" data-testid="peek-rich">
          <div className="flex items-center gap-2 text-xs">
            <CalendarClock className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="flex-1">This week <b className="font-mono">{d.week.hours}h</b> · util {d.week.utilization_pct}%</span>
          </div>
          <div className="h-1.5 bg-muted"><div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, d.week.utilization_pct)}%` }} /></div>
          <div className="grid grid-cols-3 gap-2 text-xs pt-1">
            <span className="flex items-center gap-1"><Trophy className="w-3.5 h-3.5 text-amber-500" />{d.points_total} pts{d.rank ? ` · #${d.rank}` : ""}</span>
            <span className="flex items-center gap-1"><ListChecks className="w-3.5 h-3.5 text-muted-foreground" />{d.open_tasks} open{d.overdue_tasks ? <Badge variant="outline" className="rounded-none border-0 bg-red-500/15 text-red-600 px-1 py-0 text-[10px]">{d.overdue_tasks} late</Badge> : null}</span>
            <span className="flex items-center gap-1"><AlertTriangle className={`w-3.5 h-3.5 ${d.compliance_30d.violations ? "text-red-500" : "text-emerald-500"}`} />{d.compliance_30d.violations} viol/30d</span>
          </div>
          <p className="text-xs text-muted-foreground">Next 7d planned: <b className="font-mono">{d.upcoming_planned_hours}h</b></p>
          {d.ai_one_liner && (
            <div className="border border-violet-500/30 bg-violet-500/5 p-2">
              <p className="text-[10px] uppercase tracking-wide text-violet-600 flex items-center justify-between"><span className="flex items-center gap-1"><Sparkles className="w-3 h-3" />AI insight</span>{d.ai_summary_at && <span className="normal-case text-muted-foreground">{d.ai_summary_at.slice(0, 10)}</span>}</p>
              <p className="text-xs mt-0.5 line-clamp-3">{d.ai_one_liner}</p>
            </div>
          )}
          <div className="flex gap-3 pt-1 text-xs">
            {onOpenProfile && (
              <button type="button" className="text-primary hover:underline" onClick={onOpenProfile} data-testid="peek-open-profile">Open profile →</button>
            )}
            <Link to="/app/people" className="text-primary hover:underline">People analytics</Link>
            <Link to="/app/compliance" className="text-primary hover:underline">Compliance</Link>
          </div>
        </div>
      )}
    </div>
  );
}
